import {DayName} from '@lib/utils';
import {getAllBuildings, getDefaultBuildingId, getLastSelectedWorkspaceReservationLocation, RootState} from '../../..';
import {LocalDateString, Workday} from '../apiTypes';
import {format} from 'date-fns';

export const getClosedNodesDueToOpeningDaysExceptions = (state: RootState, date: LocalDateString) => {
  // Resolve open/closed
  const relevantExceptions = state.data.openingDays.openingDaysExceptions.filter((e) => {
    if (e.startDate > date) return false;
    if (e.endDate < date) return false;
    return true;
  });

  return new Set(relevantExceptions.flatMap((e) => e.closedNodeIds));
};

export const getBuildingIdsThatRequireWorkspaceBooking = (state: RootState) => {
  return new Set(
    getAllBuildings(state)
      .filter((b) => !b.allowWorkdayWithoutWorkspace)
      .map((b) => b.id),
  );
};

const buildingPriority =
  (
    workdayBuildingId: string | undefined,
    lastSelectedBuildingId: string | undefined,
    defaultBuildingId: string | undefined,
  ) =>
  (buildingId: string): number => {
    if (buildingId === workdayBuildingId) return 100;
    if (buildingId === lastSelectedBuildingId) return 10;
    if (buildingId === defaultBuildingId) return 1;
    return 0;
  };

export const getBuildingSections = (state: RootState, date: LocalDateString, workday?: Workday | null) => {
  const closedIds = getClosedNodesDueToOpeningDaysExceptions(state, date);

  const workdayBuildingId = workday?.status === 'OfficeDay' ? workday.buildingId : undefined;
  const lastSelectedBuildingId = getLastSelectedWorkspaceReservationLocation(state).buildingId;
  const defaultBuildingId = getDefaultBuildingId(state);

  const prio = buildingPriority(workdayBuildingId, lastSelectedBuildingId, defaultBuildingId);

  const buildings = getAllBuildings(state).sort((a, b) => prio(b.id) - prio(a.id) || a.name.localeCompare(b.name));

  const weekday = format(new Date(date), 'EEEE') as DayName;

  const open: typeof buildings = [];
  const closed: typeof buildings = [];

  for (const building of buildings) {
    // Building opening days
    const openOnWeekday = building.openingDays.includes(weekday);
    if (!openOnWeekday) {
      closed.push(building);
      continue;
    }

    // Opening day exceptions
    // Note: we don't do the expensive "all floors are closed" check here, since
    // we're only booking workdays (not workspace reservations)
    if (closedIds.has(building.id)) {
      closed.push(building);
      continue;
    }

    open.push(building);
  }

  return {
    open,
    closed,
  };
};
